import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  ApplicationWithDetails,
  CreateApplicationRequest,
  UpdateApplicationStatusRequest,
} from '../../services/unified/types';

/**
 * Query hook for getting user applications with enhanced details
 */
export const useApplications = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.applications.lists(),
    queryFn: (): Promise<ApplicationWithDetails[]> => {
      return unifiedApiService.applications.getUserApplications();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled, // Only fetch when enabled (user is authenticated)
  });
};

/**
 * Query hook for getting a specific application by ID
 */
export const useApplicationDetails = (id: string) => {
  return useQuery({
    queryKey: queryKeys.applications.detail(id),
    queryFn: (): Promise<ApplicationWithDetails> => {
      return unifiedApiService.applications.getApplicationDetails(id);
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for creating a new application
 */
export const useCreateApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateApplicationRequest) => {
      return unifiedApiService.applications.createApplication(data);
    },
    onSuccess: () => {
      // Invalidate applications list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
};

/**
 * Mutation hook for updating application status (admin only)
 */
export const useUpdateApplicationStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateApplicationStatusRequest }) => {
      return unifiedApiService.applications.updateApplicationStatus(id, data);
    },
    onSuccess: () => {
      // Invalidate applications list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
};

/**
 * Mutation hook for withdrawing an application
 */
export const useWithdrawApplication = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => {
      return unifiedApiService.applications.withdrawApplication(id);
    },
    onSuccess: () => {
      // Invalidate applications list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.applications.all });
    },
  });
};

/**
 * Query hook for checking if user has applied to a specific job
 * Returns the application if found, null otherwise
 */
export const useHasAppliedToJob = (jobId: string, enabled: boolean = true) => {
  const { data: applications, isLoading } = useApplications(enabled);
  
  const application = applications?.find(app => app.job_id === jobId) || null;
  
  return {
    hasApplied: !!application,
    application,
    isLoading: enabled ? isLoading : false,
  };
};
